import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { useCurrency } from '../CurrencyContext';
import './TransactionModal.css';

const CATEGORIES = [
  { id: 'rent', icon: '🏠', label: 'Rent' },
  { id: 'groceries', icon: '🛒', label: 'Groceries' },
  { id: 'utilities', icon: '⚡', label: 'Utilities' },
  { id: 'metro', icon: '🚇', label: 'Metro' },
  { id: 'health', icon: '💊', label: 'Health' },
  { id: 'dining', icon: '🍔', label: 'Dining Out' },
  { id: 'food', icon: '🍱', label: 'Food' },
  { id: 'shopping', icon: '🛍️', label: 'Shopping' },
  { id: 'entertainment', icon: '🎬', label: 'Entertainment' },
  { id: 'travel', icon: '✈️', label: 'Travel' },
  { id: 'savings', icon: '💰', label: 'Savings' },
  { id: 'investments', icon: '📈', label: 'Investments' },
  { id: 'emi', icon: '🏦', label: 'EMI / Loan' },
  { id: 'other', icon: '📦', label: 'Other' },
];

const RATE_TYPES = [
  { id: 'zero',    label: '0% Interest' },
  { id: 'monthly', label: 'Monthly %' },
  { id: 'yearly',  label: 'Yearly %' },
  { id: 'flat',    label: 'Flat Rate' },
];

function addMonths(dateStr, months) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const maxDay = new Date(ny, nm, 0).getDate();
  return `${ny}-${String(nm).padStart(2, '0')}-${String(Math.min(d, maxDay)).padStart(2, '0')}`;
}

function calcEMISchedule(principal, rateType, rateValue, tenure, firstDate) {
  const p = parseFloat(principal) || 0;
  const n = parseInt(tenure) || 1;
  const r = parseFloat(rateValue) || 0;
  if (p <= 0 || n <= 0) return [];

  let amounts = [];

  if (rateType === 'zero' || r === 0) {
    const amt = +(p / n).toFixed(2);
    amounts = Array(n).fill(amt);
    amounts[n - 1] = +(p - amt * (n - 1)).toFixed(2);
  } else if (rateType === 'flat') {
    const totalInterest = p * (r / 100) * (n / 12);
    const amt = +((p + totalInterest) / n).toFixed(2);
    amounts = Array(n).fill(amt);
    amounts[n - 1] = +(p + totalInterest - amt * (n - 1)).toFixed(2);
  } else {
    const mr = rateType === 'monthly' ? r / 100 : r / 100 / 12;
    const emi = +(p * mr * Math.pow(1 + mr, n) / (Math.pow(1 + mr, n) - 1)).toFixed(2);
    amounts = Array(n).fill(emi);
    // Correct last installment for reducing balance
    let bal = p;
    for (let i = 0; i < n - 1; i++) {
      const interest = +(bal * mr).toFixed(2);
      bal = +(bal - (emi - interest)).toFixed(2);
    }
    amounts[n - 1] = +(bal * (1 + mr)).toFixed(2);
  }

  return amounts.map((amt, i) => ({
    date: addMonths(firstDate, i),
    amount: amt,
    no: i + 1,
  }));
}

function fmtMonthYear(dateStr) {
  const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

export default function TransactionModal({ transaction, onClose, onUpdate }) {
  const { currency } = useCurrency();
  const sym = currency?.symbol || '₹';

  const [amount, setAmount]           = useState(transaction.amount || '');
  const [category, setCategory]       = useState(transaction.category || '');
  const [description, setDescription] = useState(transaction.description || '');
  const [date, setDate]               = useState(transaction.transaction_date || '');
  const [dashboardId, setDashboardId] = useState(transaction.dashboard_id || '');
  const [accountId, setAccountId]     = useState(transaction.account_id || '');
  const [tagId, setTagId]             = useState(transaction.tag_id || '');

  const [dashboards, setDashboards] = useState([]);
  const [accounts, setAccounts]     = useState([]);
  const [tags, setTags]             = useState([]);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const [metadata, setMetadata]     = useState([]);
  const [metaLoading, setMetaLoading] = useState(true);

  // active side-panel: null | 'emi' | 'return'
  const [panel, setPanel] = useState(null);

  // EMI form
  const [emiStep, setEmiStep]           = useState('form'); // 'form' | 'confirm'
  const [emiRateType, setEmiRateType]   = useState('zero');
  const [emiRate, setEmiRate]           = useState('');
  const [emiTenure, setEmiTenure]       = useState('6');
  const [emiFirstDate, setEmiFirstDate] = useState(todayStr());
  const [emiCashback, setEmiCashback]   = useState('');
  const [emiSaving, setEmiSaving]       = useState(false);

  // Return form
  const [retAmount, setRetAmount] = useState(transaction.amount || '');
  const [retDate, setRetDate]     = useState(todayStr());
  const [retNote, setRetNote]     = useState('');
  const [retSaving, setRetSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/api/dashboards'); setDashboards(data || []); } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    if (dashboardId) {
      (async () => {
        try { 
          const [accs, tgs] = await Promise.all([
            api.get(`/api/accounts?dashboard_id=${dashboardId}`),
            api.get(`/api/tags?dashboard_id=${dashboardId}`)
          ]);
          setAccounts(accs.data || []);
          setTags(tgs.data || []);
        } catch (_) {}
      })();
    }
  }, [dashboardId]);

  const fetchMeta = useCallback(async () => {
    setMetaLoading(true);
    try { const { data } = await api.get(`/api/transactions/${transaction.id}/metadata`); setMetadata(data || []); } catch (_) {}
    setMetaLoading(false);
  }, [transaction.id]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const isDebit   = transaction.transaction_type === 'debit';
  const emiParent = metadata.find(m => m.flag_type === 'emi_parent');
  const returned  = metadata.find(m => m.flag_type === 'returned');
  const isEmiChild = metadata.some(m => m.flag_type === 'emi_child');

  // Computed EMI schedule (only when form is valid)
  const emiSchedule = useMemo(() => {
    if (!emiFirstDate || !emiTenure || parseInt(emiTenure) < 1) return [];
    return calcEMISchedule(transaction.amount, emiRateType, emiRate, emiTenure, emiFirstDate);
  }, [transaction.amount, emiRateType, emiRate, emiTenure, emiFirstDate]);

  const emiTotal    = useMemo(() => +emiSchedule.reduce((s, e) => s + e.amount, 0).toFixed(2), [emiSchedule]);
  const emiMonthly  = emiSchedule[0]?.amount || 0;
  const emiInterest = +(emiTotal - parseFloat(transaction.amount || 0)).toFixed(2);
  const emiEndDate  = emiSchedule[emiSchedule.length - 1]?.date || '';

  const rateLabel = emiRateType === 'zero'    ? '0% interest' :
                    emiRateType === 'monthly' ? `${emiRate}% / month` :
                    emiRateType === 'yearly'  ? `${emiRate}% / year` :
                                               `${emiRate}% flat`;

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await api.patch(`/api/transactions/${transaction.id}`, {
        amount: parseFloat(amount), category, description,
        transaction_date: date, account_id: accountId, dashboard_id: dashboardId,
        tag_id: tagId || null,
      });
      onUpdate(); onClose();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this transaction?')) return;
    setSaving(true); setError('');
    try { await api.delete(`/api/transactions/${transaction.id}`); onUpdate(); onClose(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to delete'); setSaving(false); }
  };

  const handleConvertEMI = async () => {
    setEmiSaving(true); setError('');
    try {
      await api.post(`/api/transactions/${transaction.id}/convert-emi`, {
        rate_type: emiRateType,
        rate_value: parseFloat(emiRate) || 0,
        tenure: parseInt(emiTenure),
        first_emi_date: emiFirstDate,
        cashback: parseFloat(emiCashback) || 0,
        account_id: accountId,
        dashboard_id: dashboardId,
      });
      await fetchMeta();
      setPanel(null);
      setEmiStep('form');
      onUpdate();
    } catch (err) { setError(err.response?.data?.error || 'Failed to convert to EMI'); }
    setEmiSaving(false);
  };

  const handleReturn = async () => {
    setRetSaving(true); setError('');
    try {
      await api.post(`/api/transactions/${transaction.id}/return`, {
        return_amount: parseFloat(retAmount),
        return_date: retDate, note: retNote,
        account_id: accountId, dashboard_id: dashboardId,
      });
      await fetchMeta(); setPanel(null); onUpdate();
    } catch (err) { setError(err.response?.data?.error || 'Failed to mark return'); }
    setRetSaving(false);
  };

  const cat = CATEGORIES.find(c => c.id === transaction.category) || { icon: '📦', label: transaction.category };

  return (
    <div className="txm-overlay" onClick={onClose}>
      <div className="txm-panel" onClick={e => e.stopPropagation()}>
        <button className="txm-close" onClick={onClose}>✕</button>

        {/* ── Transaction summary header ── */}
        <div className="txm-summary">
          <span className="txm-summary-icon">{cat.icon}</span>
          <div className="txm-summary-info">
            <div className="txm-summary-desc">{transaction.description || cat.label}</div>
            <div className="txm-summary-sub">
              {transaction.accounts?.name || ''} · {(transaction.transaction_date?.includes('T') ? new Date(transaction.transaction_date) : new Date(transaction.transaction_date + 'T00:00:00')).toLocaleDateString()}
            </div>
          </div>
          <div className={`txm-summary-amt ${isDebit ? 'debit' : 'credit'}`}>
            {isDebit ? '-' : '+'}{sym}{parseFloat(transaction.amount).toFixed(2)}
          </div>
        </div>

        {/* ── Status badges ── */}
        {!metaLoading && (
          <div className="txm-badges">
            {emiParent && (
              <span className="txm-badge emi">
                🏦 EMI · {emiParent.metadata?.tenure} mo · {sym}{emiParent.metadata?.monthly_amount}/mo
                {emiParent.metadata?.cashback_expected > 0 &&
                  <span className="txm-badge-cashback"> · ↩ {sym}{emiParent.metadata.cashback_expected} cashback</span>}
              </span>
            )}
            {returned && (
              <span className="txm-badge returned">↩️ Returned · {sym}{returned.metadata?.return_amount}</span>
            )}
            {isEmiChild && (
              <span className="txm-badge emi-child">🏦 EMI Installment</span>
            )}
          </div>
        )}

        {/* ── Action pills ── */}
        {!metaLoading && (
          <div className="txm-actions-top">
            <button
              className={`txm-action-pill comment${panel === 'comment' ? ' active' : ''}`}
              onClick={() => setPanel(p => p === 'comment' ? null : 'comment')}
            >
              💬 Comment
            </button>
            {isDebit && !emiParent && !isEmiChild && (
              <button
                className={`txm-action-pill emi${panel === 'emi' ? ' active' : ''}`}
                onClick={() => { setPanel(p => p === 'emi' ? null : 'emi'); setEmiStep('form'); }}
              >
                🏦 Convert to EMI
              </button>
            )}
            {!returned && (
              <button
                className={`txm-action-pill return${panel === 'return' ? ' active' : ''}`}
                onClick={() => setPanel(p => p === 'return' ? null : 'return')}
              >
                ↩️ Mark as Returned
              </button>
            )}
          </div>
        )}

        {/* ── EMI Panel ── */}
        {panel === 'emi' && (
          <div className="txm-sub-panel">

            {/* STEP 1: Form */}
            {emiStep === 'form' && (
              <>
                <div className="txm-sub-title">Convert to EMI</div>

                <div className="txm-field">
                  <label>Interest Type</label>
                  <div className="txm-rate-grid">
                    {RATE_TYPES.map(rt => (
                      <button key={rt.id}
                        className={`txm-rate-btn${emiRateType === rt.id ? ' active' : ''}`}
                        onClick={() => setEmiRateType(rt.id)}>{rt.label}</button>
                    ))}
                  </div>
                </div>

                {emiRateType !== 'zero' && (
                  <div className="txm-field">
                    <label>
                      {emiRateType === 'monthly' ? 'Monthly Rate (%)' :
                       emiRateType === 'yearly'  ? 'Annual Rate (%)'  : 'Flat Rate (%)'}
                    </label>
                    <input type="number" min="0" step="0.01" className="txm-input"
                      placeholder="e.g. 1.5" value={emiRate}
                      onChange={e => setEmiRate(e.target.value)} />
                  </div>
                )}

                <div className="txm-row">
                  <div className="txm-field">
                    <label>Tenure (months)</label>
                    <input type="number" min="1" max="120" className="txm-input"
                      value={emiTenure} onChange={e => setEmiTenure(e.target.value)} />
                  </div>
                  <div className="txm-field">
                    <label>First EMI Date</label>
                    <input type="date" className="txm-input" value={emiFirstDate}
                      onChange={e => setEmiFirstDate(e.target.value)} />
                  </div>
                </div>

                {/* live mini-preview */}
                {emiSchedule.length > 0 && (
                  <div className="txm-emi-preview mini">
                    <div className="txm-emi-preview-row">
                      <span>Monthly EMI</span>
                      <strong>{sym}{emiMonthly.toFixed(2)}</strong>
                    </div>
                    <div className="txm-emi-preview-row">
                      <span>Total</span>
                      <strong>{sym}{emiTotal.toFixed(2)}</strong>
                    </div>
                    {emiInterest > 0 && (
                      <div className="txm-emi-preview-row interest">
                        <span>Interest charged</span>
                        <strong>+{sym}{emiInterest.toFixed(2)}</strong>
                      </div>
                    )}
                  </div>
                )}

                <button className="txm-confirm-btn emi"
                  disabled={emiSchedule.length === 0}
                  onClick={() => setEmiStep('confirm')}>
                  Review EMI Plan →
                </button>
              </>
            )}

            {/* STEP 2: Confirmation */}
            {emiStep === 'confirm' && (
              <>
                <div className="txm-confirm-header">
                  <button className="txm-back-btn" onClick={() => setEmiStep('form')}>← Edit</button>
                  <div className="txm-sub-title">Confirm EMI Plan</div>
                </div>

                {/* Breakdown */}
                <div className="txm-breakdown">
                  <div className="txm-breakdown-row">
                    <span>Purchase Amount</span>
                    <strong>{sym}{parseFloat(transaction.amount).toFixed(2)}</strong>
                  </div>
                  <div className="txm-breakdown-row">
                    <span>Interest Rate</span>
                    <strong>{rateLabel}</strong>
                  </div>
                  <div className="txm-breakdown-row">
                    <span>Tenure</span>
                    <strong>{emiTenure} months</strong>
                  </div>
                  <div className="txm-breakdown-row">
                    <span>Monthly EMI</span>
                    <strong>{sym}{emiMonthly.toFixed(2)}</strong>
                  </div>
                  <div className="txm-breakdown-row">
                    <span>First EMI</span>
                    <strong>{fmtMonthYear(emiFirstDate)}</strong>
                  </div>
                  <div className="txm-breakdown-row">
                    <span>Last EMI</span>
                    <strong>{fmtMonthYear(emiEndDate)}</strong>
                  </div>
                  <div className="txm-breakdown-divider" />
                  <div className="txm-breakdown-row total">
                    <span>Total Payable</span>
                    <strong>{sym}{emiTotal.toFixed(2)}</strong>
                  </div>
                </div>

                {/* Extra cost callout */}
                {emiInterest > 0 ? (
                  <div className="txm-extra-cost">
                    <div className="txm-extra-label">Extra you're paying</div>
                    <div className="txm-extra-value">+{sym}{emiInterest.toFixed(2)}</div>
                    <div className="txm-extra-sub">
                      {((emiInterest / parseFloat(transaction.amount)) * 100).toFixed(1)}% over purchase price
                    </div>
                  </div>
                ) : (
                  <div className="txm-extra-cost zero">
                    <div className="txm-extra-label">Zero-cost EMI</div>
                    <div className="txm-extra-value zero">No extra charges</div>
                  </div>
                )}

                {/* EMI Schedule */}
                <div className="txm-schedule">
                  <div className="txm-schedule-title">EMI Schedule</div>
                  <div className="txm-schedule-list">
                    {emiSchedule.map(e => (
                      <div key={e.no} className="txm-schedule-row">
                        <span className="txm-schedule-no">#{e.no}</span>
                        <span className="txm-schedule-date">{fmtMonthYear(e.date)}</span>
                        <span className="txm-schedule-amt">{sym}{e.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cashback input */}
                <div className="txm-cashback-section">
                  <div className="txm-cashback-title">
                    <span>Expected Cashback</span>
                    <span className="txm-cashback-tag">optional</span>
                  </div>
                  <div className="txm-cashback-hint">
                    Any reward points, offers, or bank cashback on this EMI?
                  </div>
                  <div className="txm-cashback-input-wrap">
                    <span className="txm-cashback-sym">{sym}</span>
                    <input type="number" min="0" step="0.01"
                      className="txm-cashback-input"
                      placeholder="0.00"
                      value={emiCashback}
                      onChange={e => setEmiCashback(e.target.value)} />
                  </div>
                  {parseFloat(emiCashback) > 0 && emiInterest > 0 && (
                    <div className="txm-cashback-net">
                      Net extra cost after cashback:{' '}
                      <strong>
                        {sym}{Math.max(0, emiInterest - parseFloat(emiCashback)).toFixed(2)}
                      </strong>
                    </div>
                  )}
                </div>

                {error && <div className="txm-error">{error}</div>}

                <button className="txm-confirm-btn emi" onClick={handleConvertEMI} disabled={emiSaving}>
                  {emiSaving
                    ? 'Creating EMIs…'
                    : `Confirm — ${emiTenure} EMIs of ${sym}${emiMonthly.toFixed(2)}`}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Return Panel ── */}
        {panel === 'return' && (
          <div className="txm-sub-panel">
            <div className="txm-sub-title">Mark as Returned</div>
            <div className="txm-row">
              <div className="txm-field">
                <label>Return Amount</label>
                <input type="number" min="0" step="0.01" className="txm-input"
                  value={retAmount} onChange={e => setRetAmount(e.target.value)} />
              </div>
              <div className="txm-field">
                <label>Return Date</label>
                <input type="date" className="txm-input" value={retDate}
                  onChange={e => setRetDate(e.target.value)} />
              </div>
            </div>
            <div className="txm-field">
              <label>Note (optional)</label>
              <input type="text" className="txm-input"
                placeholder="e.g. Returned at store" value={retNote}
                onChange={e => setRetNote(e.target.value)} />
            </div>
            {error && <div className="txm-error">{error}</div>}
            <button className="txm-confirm-btn return" onClick={handleReturn} disabled={retSaving}>
              {retSaving ? 'Processing…' : `Confirm Return of ${sym}${parseFloat(retAmount || 0).toFixed(2)}`}
            </button>
          </div>
        )}

        {/* ── Comment / Chat Panel ── */}
        {panel === 'comment' && (
          <div className="txm-sub-panel">
            <div className="txm-sub-title">Comments & Chat</div>
            <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: '0.9rem' }}>
              Transaction messaging thread will appear here.
            </div>
            <div className="txm-field">
              <input type="text" className="txm-input" placeholder="Type a message..." />
            </div>
            <button className="txm-confirm-btn comment" style={{ background: '#38bdf8', color: '#fff', border: 'none', padding: '13px', borderRadius: '14px', fontWeight: '700', marginTop: '4px' }}>
              Send
            </button>
          </div>
        )}

        {/* ── Edit Form ── */}

        <div className="txm-divider" />
        <h2 className="txm-title">Edit Transaction</h2>

        <div className="txm-form">
          <div className="txm-field">
            <label>Amount</label>
            <input type="number" className="txm-input amount" value={amount}
              onChange={e => setAmount(e.target.value)} />
          </div>
          <div className="txm-field">
            <label>Category</label>
            <div className="txm-cat-grid">
              {CATEGORIES.map(c => (
                <button key={c.id}
                  className={`txm-cat-btn${category === c.id ? ' active' : ''}`}
                  onClick={() => setCategory(c.id)}>
                  <span>{c.icon}</span> {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="txm-row">
            <div className="txm-field">
              <label>Dashboard</label>
              <select value={dashboardId} onChange={e => setDashboardId(e.target.value)}>
                {dashboards.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="txm-field">
              <label>Account</label>
              <select value={accountId} onChange={e => setAccountId(e.target.value)}>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
          <div className="txm-field">
            <label>Tag</label>
            <select value={tagId} onChange={e => setTagId(e.target.value)}>
              <option value="">None</option>
              {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="txm-field">
            <label>Date</label>
            <input type="date" className="txm-input" value={date}
              onChange={e => setDate(e.target.value)} />
          </div>
          <div className="txm-field">
            <label>Description</label>
            <textarea className="txm-textarea" value={description}
              onChange={e => setDescription(e.target.value)} placeholder="Add a note…" />
          </div>
          {error && panel === null && <div className="txm-error">{error}</div>}
          <div className="txm-actions">
            <button className="txm-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Updating…' : 'Save Changes'}
            </button>
            <button className="txm-delete" onClick={handleDelete} disabled={saving}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
